/**
 * Task requests: the task, listing, dashboard and contribution shapes as
 * the server sends them, and every task read and mutation — listing,
 * detail, proposal, deletion, transitions, the Owner's edit and
 * contributor-entered references.
 */

import { request } from './request.js';
import type { TaskDependencyView } from './task-dependencies.js';

export interface TaskParticipant {
  readonly id: string;
  readonly displayName: string;
}

export interface TaskSummary {
  readonly id: string;
  readonly reference: string;
  readonly title: string;
  readonly taskType: string;
  readonly difficulty: string;
  readonly status: string;
  readonly suggestedPriority: string | null;
  readonly priority: string | null;
  readonly requiresMerge: boolean;
  readonly hasMergeReference: boolean;
  /** Derived server-side from prerequisites; never stored. */
  readonly executionState: 'INDEPENDENT' | 'BLOCKED' | 'DEPENDENCIES_CLEARED';
  /** Unresolved prerequisites the caller may see, bounded for cards. */
  readonly blockedBy: readonly { reference: string; status: string }[];
  readonly unresolvedCount: number;
  readonly proposedBy: TaskParticipant;
  readonly assignee: TaskParticipant | null;
  readonly version: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface TaskReferenceRecord {
  readonly id: string;
  readonly kind: string;
  readonly url: string;
  readonly enteredBy: TaskParticipant;
  readonly enteredAt: string;
}

/** The Data Room document a submission presented, pinned to its revision. */
export interface SubmittedDocumentRecord {
  readonly documentId: string;
  readonly slug: string;
  readonly title: string;
  readonly revisionVersion: number;
  /** Who the pinned document is for, when the server sends it. */
  readonly audience?: string;
  /** True when the living document has since been deleted; the pin stays. */
  readonly deleted: boolean;
}

export interface TaskDetail extends TaskSummary {
  readonly description: string;
  readonly approvedBy: TaskParticipant | null;
  readonly references: readonly TaskReferenceRecord[];
  readonly submittedDocument: SubmittedDocumentRecord | null;
}

export interface TaskEventRecord {
  readonly id: string;
  readonly taskId: string;
  readonly taskReference: string;
  readonly taskTitle: string;
  readonly taskType: string;
  readonly actor: TaskParticipant;
  readonly kind: string;
  readonly fromStatus: string | null;
  readonly toStatus: string | null;
  readonly note: string | null;
  readonly detail: Record<string, unknown> | null;
  readonly occurredAt: string;
}

export interface TaskListing {
  readonly tasks: readonly TaskSummary[];
  readonly counts: Readonly<Record<string, number>>;
}

export interface TaskView {
  readonly task: TaskDetail;
  readonly events: readonly TaskEventRecord[];
  readonly dependencies: readonly TaskDependencyView[];
  readonly dependents: readonly TaskDependencyView[];
  readonly availableTransitions: readonly string[];
}

export interface DashboardView {
  readonly myActiveTasks: readonly TaskSummary[];
  readonly readyForYou: readonly TaskSummary[];
  readonly waitingOnYou: readonly TaskSummary[];
  readonly proposalsPending: readonly TaskSummary[];
  readonly recentlyCompleted: readonly TaskSummary[];
  readonly counts: Readonly<Record<string, number>>;
}

export function fetchTasks(
  filter: {
    statuses?: readonly string[];
    types?: readonly string[];
  } = {},
): Promise<TaskListing> {
  const query = new URLSearchParams();
  for (const status of filter.statuses ?? []) query.append('status', status);
  for (const type of filter.types ?? []) query.append('type', type);
  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  return request('GET', `/workspace/tasks${suffix}`);
}

export function fetchTask(taskId: string): Promise<TaskView> {
  return request('GET', `/workspace/tasks/${encodeURIComponent(taskId)}`);
}

export function fetchDashboard(): Promise<DashboardView> {
  return request('GET', '/workspace/dashboard');
}

export function fetchContributions(
  options: { mine?: boolean; type?: string } = {},
): Promise<{
  events: readonly TaskEventRecord[];
}> {
  const query = new URLSearchParams();
  if (options.mine === true) query.set('mine', 'true');
  if (options.type !== undefined) query.set('type', options.type);
  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  return request('GET', `/workspace/contributions${suffix}`);
}

export function proposeTask(
  idempotencyKey: string,
  proposal: {
    title: string;
    taskType: string;
    difficulty: string;
    suggestedPriority: string;
    description: string;
    prerequisiteTaskIds?: readonly string[];
  },
): Promise<{ taskId: string }> {
  return request('POST', '/workspace/tasks', { body: proposal, idempotencyKey });
}

export function deleteTask(
  idempotencyKey: string,
  taskId: string,
  body: { expectedVersion: number },
): Promise<{ taskId: string; deleted: boolean }> {
  return request('POST', `/workspace/tasks/${encodeURIComponent(taskId)}/delete`, {
    body,
    idempotencyKey,
  });
}

export function transitionTask(
  idempotencyKey: string,
  taskId: string,
  transition:
    'approve' | 'reject' | 'claim' | 'submit' | 'complete' | 'complete_own' | 'return',
  body: Record<string, unknown>,
): Promise<{ taskId: string; status: string; version: number }> {
  return request(
    'POST',
    `/workspace/tasks/${encodeURIComponent(taskId)}/${transition}`,
    { body, idempotencyKey },
  );
}

/** An Owner's edit of a task, from any state. Not a transition. */
export function editTask(
  idempotencyKey: string,
  taskId: string,
  body: {
    expectedVersion: number;
    title?: string;
    description?: string;
    taskType?: string;
    difficulty?: string;
    suggestedPriority?: string;
    priority?: string;
    requiresMerge?: boolean;
    prerequisiteTaskIds?: readonly string[];
    unassign?: boolean;
  },
): Promise<{ taskId: string; version: number; changed: boolean }> {
  return request('POST', `/workspace/tasks/${encodeURIComponent(taskId)}/edit`, {
    body,
    idempotencyKey,
  });
}

export function addTaskReference(
  idempotencyKey: string,
  taskId: string,
  reference: { kind: string; url: string },
): Promise<{ taskId: string; added: boolean }> {
  return request('POST', `/workspace/tasks/${encodeURIComponent(taskId)}/references`, {
    body: reference,
    idempotencyKey,
  });
}
